/*
 * Copyright (c) 2026 SimplyBook.me Ltd. All rights reserved.
 * Licensed under the Apache License, Version 2.0.
 */

import React, { useState } from "react";
import { Modal, ModalBody, ModalClose, ModalContent, ModalFooter, ModalHeader, ModalTitle, ModalTrigger } from "../ui/Modal";
import Button from "../ui/Button";
import ButtonGroup from "../ui/ButtonGroup";
import Text from "../ui/Text";
import T from "../ui/Translation";
import { useDeletePaymentMethod } from "../../lib/react-query/useDeletePaymentMethod";
import { extractErrorMessage } from "../../utils/api/apiErrors";

const DeletePaymentMethodModal = ({ paymentMethod, trigger }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [error, setError] = useState(null);
  const { mutate, isPending } = useDeletePaymentMethod(paymentMethod.id);

  const handleClose = () => {
    setIsModalOpen(false);
    error && setError(null);
  };

  const handleConfirm = () => {
    mutate(undefined, {
      onSuccess: () => setIsModalOpen(false),
      onError: (err) => setError(extractErrorMessage(err, "Something went wrong")),
    });
  };

  return (
    <Modal isOpen={isModalOpen} onOpen={() => setIsModalOpen(true)} onClose={handleClose}>
      {trigger && <ModalTrigger>{trigger}</ModalTrigger>}
      <ModalContent>
        <ModalHeader>
          <ModalTitle>Remove payment method</ModalTitle>
        </ModalHeader>
        <ModalBody>
          <Text color="dark">
            {error ? error : <T name={paymentMethod.name} system={paymentMethod.payment_system}>%name% (%system%) will no longer be available for payments.</T>}
          </Text>
        </ModalBody>
        <ModalFooter>
          {!error ? (
            <ButtonGroup itemsWidth="half">
              <ModalClose>
                <Button variant="outline" title="Back" />
              </ModalClose>
              <Button variant="primary" title="Remove" onClick={handleConfirm} loading={isPending} />
            </ButtonGroup>
          ) : (
            <ButtonGroup itemsWidth="half" justify="end">
              <ModalClose>
                <Button variant="primary" title="Okay" onClick={handleClose} />
              </ModalClose>
            </ButtonGroup>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default DeletePaymentMethodModal;
